import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "../../../utils/cn";
import {
  calculatePaginationRange,
  getPageItems,
} from "./tableUtils";

export interface Pagination2Props {
  total: number;
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Hide the footer entirely when total <= pageSize. Defaults to false (explicit control). */
  autoHide?: boolean;
  className?: string;
}

const BTN_BASE =
  "w-[34px] h-[34px] rounded-[9px] grid place-items-center transition-colors select-none";
const BTN_INACTIVE =
  "bg-white border border-[var(--color-layout-dropdown-badge-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]";

const PageSizeDropdown: React.FC<{
  value: number;
  options: number[];
  onChange: (size: number) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "h-[34px] min-w-[56px] px-2.5 rounded-[9px] bg-white border border-[var(--color-layout-dropdown-badge-border)] flex items-center justify-between gap-1.5 text-[13px] font-semibold text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] hover:border-[var(--color-border-primary)] transition-all duration-150 cursor-pointer select-none",
          open && "border-[var(--color-primary-600)] ring-1 ring-[var(--color-primary-600)]"
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{value}</span>
        <ChevronDown
          className={cn(
            "w-3.5 h-3.5 text-[var(--color-text-tertiary)] transition-transform duration-200",
            open && "rotate-180 text-[var(--color-primary-600)]"
          )}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute bottom-full left-0 mb-1.5 w-full min-w-[64px] bg-white border border-[var(--color-border-secondary)] rounded-[9px] shadow-lg py-1 z-50 animate-fade-in"
        >
          {options.map((option) => {
            const isSelected = option === value;
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={cn(
                  "w-full px-2.5 py-1.5 text-left text-[12.5px] font-semibold transition-colors cursor-pointer border-none block",
                  isSelected
                    ? "bg-[var(--color-primary-50)] text-[var(--color-primary-600)] font-bold"
                    : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text-primary)]"
                )}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Pagination2 = ({
  total,
  page,
  pageSize,
  pageSizeOptions = [10, 25, 50],
  onPageChange,
  onPageSizeChange,
  autoHide = false,
  className = "",
}: Pagination2Props) => {
  if (autoHide && total <= pageSize) return null;

  const { totalPages, startItem, endItem } = calculatePaginationRange(
    total,
    page,
    pageSize
  );
  const pageItems = getPageItems(totalPages, page);

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full",
        className
      )}
    >
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="text-xs text-[var(--color-text-tertiary)] font-medium whitespace-nowrap">
          Showing {startItem}–{endItem} of {total}
        </span>
        {onPageSizeChange && (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-[var(--color-text-tertiary)] font-medium">
              Rows per page:
            </span>
            <PageSizeDropdown
              value={pageSize}
              options={pageSizeOptions}
              onChange={onPageSizeChange}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-[6px] shrink-0 max-w-full overflow-x-auto no-scrollbar py-0.5">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label="Previous page"
          className={cn(
            BTN_BASE,
            BTN_INACTIVE,
            "cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
          )}
        >
          <ChevronLeft className="w-4 h-4 text-[var(--color-text-tertiary)]" />
        </button>

        {pageItems.map((item, idx) => {
          if (typeof item === "string") {
            return (
              <span
                key={`${item}-${idx}`}
                className="w-[34px] h-[34px] grid place-items-center text-sm text-[var(--color-text-tertiary)] select-none"
              >
                …
              </span>
            );
          }
          const isCurrent = item === page;
          return (
            <button
              type="button"
              key={item}
              onClick={() => onPageChange(item)}
              aria-current={isCurrent ? "page" : undefined}
              className={cn(
                BTN_BASE,
                isCurrent
                  ? "bg-[var(--color-primary-600)] text-white font-bold border-none cursor-default"
                  : cn(BTN_INACTIVE, "font-semibold cursor-pointer")
              )}
            >
              {item}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          aria-label="Next page"
          className={cn(
            BTN_BASE,
            BTN_INACTIVE,
            "cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
          )}
        >
          <ChevronRight className="w-4 h-4 text-[var(--color-text-tertiary)]" />
        </button>
      </div>
    </div>
  );
};

export default Pagination2;
